/**
 * La baldosa de una cifra. El mismo patrón que las ventas del día en Entretur.
 *
 * Nació de un problema concreto: las tarjetas del tablero pintaban el valor
 * con `toFixed(2)` —«$17861900.00», doce caracteres sin separadores— dentro de
 * una tarjeta con ícono, sombra y degradado. En un teléfono no cabía y el
 * número quedaba cortado justo donde importa. Una tarjeta que no deja leer su
 * cifra no está informando de nada.
 *
 * La solución no es agrandar la tarjeta: es quitarle todo lo que no es el
 * dato. Fondo tintado en vez de sombra, etiqueta diminuta, y la cifra en
 * versales compactas —$17,86M— que caben en una línea a cualquier ancho. El
 * valor exacto vive en el tooltip, que es donde se consulta y no donde se lee
 * de un vistazo.
 */
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { TARJETA } from '../estilos/tarjeta';

const exacto = (v) => '$' + new Intl.NumberFormat('es-CO',
  { maximumFractionDigits: 0 }).format(Number(v || 0));

/**
 * $17.861.900 → «$17,86M»; $486.300 → «$486k».
 *
 * Se compacta siempre, no solo cuando no cabe: si el mismo dato cambia de
 * formato según el ancho de la pantalla, la fila de baldosas deja de poder
 * compararse de un vistazo, que es para lo único que sirve una fila así.
 */
export function compacto(n) {
  const v = Number(n || 0);
  if (v === 0) return '$0';
  const abs = Math.abs(v);
  const signo = v < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    const s = m >= 100 ? m.toFixed(0) : m.toFixed(2).replace(/\.?0+$/, '');
    return `${signo}$${s.replace('.', ',')}M`;
  }
  if (abs >= 1_000) return `${signo}$${Math.round(abs / 1_000)}k`;
  return `${signo}$${new Intl.NumberFormat('es-CO').format(abs)}`;
}

export default function Tile({
  label, v, color = '#1e293b', bg = '#f1f5f9', hint, onClick, unidad,
}) {
  const valor = Number(v || 0);
  // Un conteo (facturas, clientes) no lleva signo de peso ni se compacta.
  const texto = unidad === 'cantidad'
    ? new Intl.NumberFormat('es-CO').format(valor)
    : compacto(valor);
  const ayuda = [hint, unidad === 'cantidad' ? null : exacto(valor)]
    .filter(Boolean).join(' · ');

  const cuerpo = (
    <Box onClick={onClick} sx={{
      p: TARJETA.relleno, bgcolor: bg, borderRadius: TARJETA.radio,
      height: '100%', minWidth: 0, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      gap: 0.5,
      cursor: onClick ? 'pointer' : (ayuda ? 'help' : 'default'),
      transition: 'filter .15s',
      ...(onClick ? { '&:hover': { filter: 'brightness(0.97)' } } : {}),
    }}>
      <Typography sx={{
        fontSize: { xs: 9, lg: 8.5 }, color, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.2,
        alignSelf: 'flex-start',
        // Dos líneas como máximo: una etiqueta larga no debe estirar la fila.
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', wordBreak: 'break-word',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: { xs: 15, md: 14, lg: 13, xl: 15 }, fontWeight: 800, color,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums',
      }}>
        {texto}
      </Typography>
    </Box>
  );

  return ayuda
    ? <Tooltip title={ayuda} arrow>{cuerpo}</Tooltip>
    : cuerpo;
}

/**
 * La rejilla que las contiene.
 *
 * Dos por fila en el teléfono y cuatro desde la tableta: a partir de 600px el
 * menú lateral ya está oculto, así que hay ancho de sobra y cuatro baldosas
 * de ~170px se comparan de un vistazo. `minmax(0, 1fr)` es lo que permite que
 * una columna se encoja por debajo de su contenido —sin él, una cifra larga
 * ensancha la fila entera.
 */
export const REJILLA_TILES = {
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))',
                         sm: 'repeat(4, minmax(0, 1fr))' },
  gap: 1,
};

/**
 * Pesos, como se escriben en Colombia: «$17.861.900».
 *
 * Toda la aplicación venía pintando los importes con `toFixed(2)` —
 * «$17861900.00»—: sin separadores, con dos decimales que en pesos nadie usa,
 * y ocupando doce caracteres. En una celda estrecha o en un teléfono el número
 * se cortaba justo donde importa.
 */
export const pesos = (v) => '$' + new Intl.NumberFormat('es-CO',
  { maximumFractionDigits: 0 }).format(Number(v || 0));
